import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ChevronRight, Rocket, Mail } from "lucide-react";
import WorkflowDiagram from "@/components/WorkflowDiagram";

const SCREENS = [
  {
    title: "The Problem",
    subtitle: "Fragmented physical-product development",
    body: "Physical-product development is divided among disconnected CAD applications, simulation programs, engineering specialists, material databases, suppliers, and manufacturing systems. Teams lose time reconciling the gaps.",
  },
  {
    title: "The A.I.U.D.E. Workflow",
    subtitle: "One guided path — intent to manufacturing preparation",
    body: "A.I.U.D.E. is being developed to connect the seven stages of physical-product development into a single traceable pipeline.",
    stage: "specification",
  },
  {
    title: "Working Project Intake",
    subtitle: "Ordinary language becomes editable engineering specification",
    body: "Founders describe what they want. A.I.U.D.E. converts that description into a structured, editable engineering specification. The user reviews, revises, and approves before the workflow proceeds.",
  },
  {
    title: "Validated Evidence",
    subtitle: "CalculiX Mechanical Mounting Bracket",
    body: "One verified engineering result set produced by A.I.U.D.E.'s solver pipeline. All values shown are from the actual solver run; nothing has been invented.",
    stats: [
      ["Solver", "CalculiX"],
      ["Exit code", "0"],
      ["Max displacement", "0.4147 mm"],
      ["Max stress", "34.31 MPa"],
      ["Yield strength", "276 MPa"],
      ["σ_yield / σ_max", "≈ 8.04×"],
    ],
  },
  {
    title: "Business Potential",
    subtitle: "Accessible engineering workflow",
    body: "A.I.U.D.E. is being developed to serve inventors, hardware startups, product-development teams, small and midsized manufacturers, and engineering/fabrication partners who need a coherent, traceable path from idea to production preparation.",
  },
  {
    title: "Expansion Path",
    subtitle: "Three stages of connective intelligence",
    stages: [
      ["Stage 1", "AI-guided design, engineering coordination, validation, and manufacturing preparation."],
      ["Stage 2", "Connection to physical proof-of-concept and distributed manufacturing capabilities."],
      ["Stage 3", "Broader advanced-manufacturing infrastructure."],
    ],
    finale: true,
  },
];

export default function Investor() {
  const [i, setI] = useState(0);
  const s = SCREENS[i];
  const next = () => setI((v) => Math.min(v + 1, SCREENS.length - 1));
  const prev = () => setI((v) => Math.max(v - 1, 0));

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-tech mb-2">Investor Demonstration</div>
          <h1 className="text-2xl font-semibold text-white">Three-Minute Guided Presentation</h1>
        </div>
        <div className="flex items-center gap-2 font-mono text-xs text-slate-500">
          Screen {i + 1} / {SCREENS.length}
        </div>
      </div>

      {/* progress */}
      <div className="flex gap-1">
        {SCREENS.map((_, idx) => (
          <div key={idx} className={`h-1 flex-1 ${idx <= i ? "bg-[#00D2FF]" : "bg-[#1E2F48]"}`} />
        ))}
      </div>

      <div className="panel-elev tick-brackets p-8 lg:p-14 min-h-[420px]" data-testid={`investor-screen-${i + 1}`}>
        <div className="font-tech text-[#C9A961] mb-4">{String(i + 1).padStart(2, "0")} · Screen</div>
        <h2 className="text-3xl lg:text-4xl font-semibold text-white leading-tight">{s.title}</h2>
        <div className="mt-2 text-[#00D2FF] font-mono text-sm">{s.subtitle}</div>
        {s.body && <p className="mt-8 text-slate-300 text-lg leading-relaxed max-w-3xl">{s.body}</p>}

        {s.stage && <div className="mt-10"><WorkflowDiagram activeId={s.stage} compact /></div>}

        {s.stats && (
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {s.stats.map(([k, v]) => (
              <div key={k} className="panel p-4">
                <div className="metric-label">{k}</div>
                <div className="font-mono text-xl text-white mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        {s.stages && (
          <div className="mt-10 space-y-4">
            {s.stages.map(([k, v]) => (
              <div key={k} className="panel p-5">
                <div className="font-tech text-[#00D2FF]">{k}</div>
                <div className="text-slate-200 mt-2">{v}</div>
              </div>
            ))}
          </div>
        )}

        {s.finale && (
          <>
            <div className="dim-line my-10" />
            <p className="text-xl text-slate-100 leading-relaxed max-w-3xl">
              <span className="text-[#C9A961]">A.I.U.D.E. is not another design tool.</span> It is being developed as the connective intelligence between an idea and the systems required to build it.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/intake" className="btn-primary" data-testid="investor-cta-start">Start a Project <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/validated-demo" className="btn-ghost" data-testid="investor-cta-demo">View Demonstration</Link>
              <Link to="/pilot" className="btn-copper" data-testid="investor-cta-pilot"><Rocket className="w-4 h-4" /> Request a Pilot</Link>
              <a href={`mailto:8269Dragons@gmail.com`} className="btn-ghost" data-testid="investor-cta-founder"><Mail className="w-4 h-4" /> Contact the Founder</a>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={prev} disabled={i === 0} className="btn-ghost disabled:opacity-40" data-testid="investor-prev"><ArrowLeft className="w-4 h-4" /> Previous</button>
        <button onClick={next} disabled={i === SCREENS.length - 1} className="btn-primary disabled:opacity-40" data-testid="investor-next">Next <ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
